import { Box, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { DataGrid } from '@mui/x-data-grid'
import { useEffect, useMemo, useState } from 'react'
import { format } from 'date-fns';

import { isValidUser } from '../OnlyWithFunctions'
import { LOCALE_TEXT_ITA } from './LOCALE_TEXT_ITA'

const ResultsTableV2 = ({ listaPratiche, isFetchingListaPratiche, onRowClick, rowCount, totalElements, pageSize, handleChangePage, handleChangeSorting, page }) => {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });

  const theme = useTheme()

  const columns = useMemo(
    () => [
      {
        field: 'ndg',
        headerName: 'NDG',
        type: 'string',
        filterable: false,
        flex: 1,
        valueGetter: params => params.row['ndg'],
        renderCell: params => (
          <Tooltip
            title={params.row['ndg']}
            followCursor
          >
            <Typography className="table-cell-trucate">
              {params.row['ndg']}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'codiceFiscale',
        headerName: 'Codice Fiscale',
        filterable: false,
        flex: 1,
        valueGetter: params =>
          params.row['codiceFiscale'],

        renderCell: params => (
          <Tooltip
            title={params.row['codiceFiscale']}
            followCursor
          >
            <Typography className="table-cell-trucate">
              {params.row['codiceFiscale']}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'ragioneSociale',
        headerName: 'Ragione Sociale',
        filterable: false,
        flex: 1,
        valueGetter: params =>
          params.row['ragioneSociale'],

        renderCell: params => (
          <Tooltip
            title={params.row['ragioneSociale']}
            followCursor
          >
            <Typography className="table-cell-trucate">
              {params.row['ragioneSociale']}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'daAssegnare',
        headerName: 'Sale/Field Analyst',
        sortable: false,
        filterable: false,
        hide: (isValidUser('UTENTE_BASE') || isValidUser('FIELD_ANALYST')),
        flex: 1,
        valueGetter: params =>
          params.row['daAssegnare'],

        renderCell: params => (
          <Tooltip
            title={params.row['daAssegnare']}
            followCursor
          >
            <Typography className="table-cell-trucate">
              {params.row['daAssegnare']}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'dataCreazione',
        type: 'date',
        headerName: 'Data Apertura',
        filterable: false,
        flex: 1,
        valueGetter: params => params?.row?.createdAt,

        renderCell: params =>
          params?.row.dataCreazione ? format(new Date(params?.row.dataCreazione), "dd/MM/yyyy") : "",
      },
      {
        field: 'dataChiusura',
        headerName: 'Data Chiusura',
        filterable: false,
        flex: 1,
        valueGetter: params =>
          params.row['dataChiusura'],
        renderCell: params => (
          <Typography className="table-cell-truncate">
            {params?.row['dataChiusura']
              ? new Date(params.row['dataChiusura']).toLocaleDateString()
              : ""
            }
          </Typography>

        ),
      },
      {
        field: 'statoPratica',
        headerName: 'Stato Pratica',
        sortable: true,
        filterable: false,
        flex: 1,
        valueGetter: params =>
          params.row['statoPratica'],
        renderCell: params => (
          <Tooltip
            title={params.row['statoPratica']}
            followCursor
          >
            <Typography className="table-cell-trucate">
              {params.row['statoPratica']}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'tipoInserimento',
        headerName: 'Tipo Inserimento',
        sortable: false,
        filterable: false,
        flex: 1,
        valueGetter: params =>
          params.row['tipoInserimento'],
        renderCell: params => (
          <Tooltip
            title={params.row['tipoInserimento']}
            followCursor
          >
            <Typography className="table-cell-trucate">
              {params.row['tipoInserimento']}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'richiestaSupporto',
        headerName: 'Richiesta Consulenza',
        sortable: false,
        filterable: false,
        flex: 1,
        valueGetter: params => params.row['richiestaSupporto'] ? 'Si' : 'No',
        renderCell: params => (
          <Tooltip title={params.row['richiestaConsulenza'] || 'N/A'} followCursor>
            <Box display="flex" justifyContent="center" alignItems="center" width="100%">
              <Typography className="table-cell-truncate">
                {params.row['richiestaSupporto'] ? "Si" : "No"}
              </Typography>
            </Box>
          </Tooltip>
        ),
      },

      {
        field: 'esitoMonitoraggio',
        headerName: 'Esito Monitoraggio',
        sortable: true,
        filterable: false,
        hide: !isValidUser('UTENTE_INTERNO'),
        flex: 1,
        valueGetter: params => {
          return params.row['esitoMonitoraggio'] ? 'Si' : 'No';
        },
        renderCell: params => {
          const esitoMonitoraggio = params.row['esitoMonitoraggio']; // booleano

          return (
            <Tooltip
              title={esitoMonitoraggio !== undefined ? (esitoMonitoraggio ? 'Si' : 'No') : 'N/A'}
              followCursor
            >
              <Box display="flex" justifyContent="center" alignItems="center" width="100%">
                <Typography className="table-cell-truncate">
                  {esitoMonitoraggio ? 'Si' : 'No'}
                </Typography>
              </Box>
            </Tooltip>
          );
        },

      },

    ],
    [],
  )

  return (
    <Box
      sx={{
        height: 526,
        width: '100%',
        '& .utenteBase': {
          backgroundColor: theme.palette.grey.light,
          color: theme.palette.black,
        },
        '& .fieldAnalyst': {
          backgroundColor: theme.palette.blue.light,
          color: theme.palette.black,
        },
      }}
    >
      <DataGrid
        disableSelectionOnClick
        sx={{ cursor: 'url(hand.cur), pointer' }}
        rows={listaPratiche}
        columns={columns}
        pageSize={pageSize}
        rowsPerPageOptions={[8]}
        loading={isFetchingListaPratiche}
        getRowId={row => row.id}
        localeText={LOCALE_TEXT_ITA}
        onRowClick={(selectedRow) => onRowClick(selectedRow.row)}
        rowCount={totalElements}
        sortingOrder={['asc', 'desc']}
        onPageChange={(page) => handleChangePage(page)}
        page={page}
        paginationModel={paginationModel}
        paginationMode="server"
        onPaginationModelChange={setPaginationModel}
        sortingMode="server"
        onSortModelChange={(sorting) => {
          handleChangeSorting(sorting[0]);
        }
        }
      />
    </Box>
  )
};

export default ResultsTableV2
